// src/errors.ts
var XONE_CSS_ERROR_CODES = {
  /** Sintaxis CSS XOne malformada (llaves, dos puntos, valores invalidos). */
  SYNTAX_ERROR: "XONE.CSS.SYNTAX_ERROR",
  /** Unidad no permitida (px, em, rem, vh, vw, pt, cm, mm, in). */
  INVALID_UNIT: "XONE.CSS.INVALID_UNIT",
  /** Color hexadecimal invalido (longitud o caracteres). */
  INVALID_COLOR: "XONE.CSS.INVALID_COLOR",
  /** Ciclo detectado al resolver extends. */
  CIRCULAR_EXTENDS: "XONE.CSS.CIRCULAR_EXTENDS",
  /** Referencia extends a regla inexistente. */
  UNRESOLVED_EXTENDS: "XONE.CSS.UNRESOLVED_EXTENDS",
  /** Bloque @platform con etiqueta no reconocida. */
  INVALID_PLATFORM: "XONE.CSS.INVALID_PLATFORM"
};
var XoneCssError = class _XoneCssError extends Error {
  code;
  source;
  cause;
  constructor(message, options) {
    super(message);
    this.name = "XoneCssError";
    this.code = options.code;
    this.source = options.source === void 0 ? void 0 : options.source.length > 120 ? options.source.slice(0, 120) + "..." : options.source;
    this.cause = options.cause;
    Object.setPrototypeOf(this, _XoneCssError.prototype);
  }
};

// src/selectors.ts
var PROP_TYPE_RE = /^prop:([A-Za-z][A-Za-z0-9]*)$/;
var CLASS_RE = /^\.([A-Za-z_][\w-]*)$/;
var MULTICLASS_RE = /^(?:\.[A-Za-z_][\w-]*){2,}$/;
var CLASS_TOKEN_RE = /\.([A-Za-z_][\w-]*)/g;
var SIMPLE_RE = /^(coll|group|frame|prop)$/;
var PSEUDO_RE = /^((?:\.[A-Za-z_][\w-]*)+|coll|group|frame|prop)((?:::?[A-Za-z][\w-]*(?:\([^)]*\))?)+)$/;
var UNKNOWN_RE = /^[A-Za-z][A-Za-z0-9_-]*$/;
function parseSelector(raw) {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    throw new XoneCssError("Selector vacio.", {
      code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
      source: raw
    });
  }
  if (trimmed.includes(",")) {
    const parts = trimmed.split(",").map((p) => p.trim()).filter((p) => p.length > 0);
    if (parts.length === 0) {
      throw new XoneCssError("Selector compuesto vacio.", {
        code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
        source: raw
      });
    }
    if (parts.length === 1) {
      return parseSelector(parts[0]);
    }
    return {
      type: "compound",
      selectors: parts.map((p) => parseSelector(p))
    };
  }
  if (SIMPLE_RE.test(trimmed)) {
    return { type: trimmed };
  }
  const propTypeMatch = PROP_TYPE_RE.exec(trimmed);
  if (propTypeMatch) {
    return { type: "prop-type", propType: propTypeMatch[1] };
  }
  const pseudoMatch = PSEUDO_RE.exec(trimmed);
  if (pseudoMatch) {
    return {
      type: "pseudo",
      base: parseSelector(pseudoMatch[1]),
      pseudo: pseudoMatch[2]
    };
  }
  const classMatch = CLASS_RE.exec(trimmed);
  if (classMatch) {
    return { type: "class", className: classMatch[1] };
  }
  if (MULTICLASS_RE.test(trimmed)) {
    const classNames = [...trimmed.matchAll(CLASS_TOKEN_RE)].map((m) => m[1]);
    return { type: "multiclass", classNames };
  }
  if (UNKNOWN_RE.test(trimmed)) {
    return { type: "unknown", name: trimmed };
  }
  throw new XoneCssError(`Selector no reconocido: "${trimmed}".`, {
    code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
    source: raw
  });
}
function selectorToCss(ast) {
  switch (ast.type) {
    case "coll":
      return ".xone-coll";
    case "group":
      return ".xone-group";
    case "frame":
      return ".xone-frame";
    case "prop":
      return ".xone-prop";
    case "prop-type":
      return `.xone-prop-${ast.propType.toLowerCase()}`;
    case "class":
      return `.${ast.className}`;
    case "multiclass":
      return ast.classNames.map((c) => `.${c}`).join("");
    case "pseudo": {
      const baseCss = selectorToCss(ast.base);
      return baseCss === "" ? "" : `${baseCss}${ast.pseudo}`;
    }
    case "compound": {
      const parts = ast.selectors.filter((s) => s.type !== "unknown").map((s) => selectorToCss(s));
      return parts.join(", ");
    }
    case "unknown":
      return "";
  }
}
function selectorKey(ast) {
  switch (ast.type) {
    case "coll":
    case "group":
    case "frame":
    case "prop":
      return ast.type;
    case "prop-type":
      return `prop:${ast.propType}`;
    case "class":
      return `.${ast.className}`;
    case "multiclass":
      return [...ast.classNames].sort().map((c) => `.${c}`).join("");
    case "pseudo":
      return `${selectorKey(ast.base)}${ast.pseudo}`;
    case "compound":
      return ast.selectors.map((s) => selectorKey(s)).join(",");
    case "unknown":
      return `?${ast.name}`;
  }
}

// src/platform.ts
var VALID_PLATFORMS = [
  "phone",
  "tablet",
  "watch",
  "web",
  "ios",
  "android",
  "portrait",
  "landscape",
  "day",
  "night"
];
var ORIENTATION_TAGS = /* @__PURE__ */ new Set(["portrait", "landscape"]);
var THEME_TAGS = /* @__PURE__ */ new Set(["day", "night"]);
function assertValidPlatformTag(tag) {
  if (!VALID_PLATFORMS.includes(tag)) {
    throw new XoneCssError(
      `Plataforma desconocida "${tag}". Validas: ${VALID_PLATFORMS.join(", ")}.`,
      {
        code: XONE_CSS_ERROR_CODES.INVALID_PLATFORM,
        source: `@platform:${tag}`
      }
    );
  }
}
function platformTagToMediaQuery(tag) {
  switch (tag) {
    case "phone":
      return "(max-width: 767px)";
    case "tablet":
      return "(min-width: 768px) and (max-width: 1199px)";
    case "watch":
      return "(max-width: 320px)";
    case "web":
      return "(min-width: 1200px)";
    case "portrait":
      return "(orientation: portrait)";
    case "landscape":
      return "(orientation: landscape)";
    case "day":
      return "(prefers-color-scheme: light)";
    case "night":
      return "(prefers-color-scheme: dark)";
    case "ios":
    case "android":
      return null;
  }
}
function matchesPlatform(tag, ctx) {
  if (!ctx) {
    return true;
  }
  if (ORIENTATION_TAGS.has(tag)) {
    return ctx.orientation === void 0 || ctx.orientation === tag;
  }
  if (THEME_TAGS.has(tag)) {
    return ctx.theme === void 0 || ctx.theme === tag;
  }
  if (ctx.platform === void 0) {
    return true;
  }
  return ctx.platform === tag;
}

// src/parser.ts
function skipBrokenRule(state, ruleStart) {
  let i = ruleStart;
  while (i < state.source.length && state.source[i] !== "{" && state.source[i] !== "}") {
    i++;
  }
  if (i >= state.source.length) {
    state.index = state.source.length;
    return;
  }
  if (state.source[i] === "}") {
    state.index = i + 1;
    return;
  }
  let depth = 0;
  while (i < state.source.length) {
    const ch = state.source[i];
    if (ch === "{") {
      depth++;
    } else if (ch === "}") {
      depth--;
      if (depth === 0) {
        i++;
        break;
      }
    }
    i++;
  }
  state.index = i > ruleStart ? i : ruleStart + 1;
}
function stripComments(source) {
  return source.replace(/\/\*[\s\S]*?\*\//g, "");
}
function skipWhitespace(state) {
  while (state.index < state.source.length) {
    const ch = state.source[state.index];
    if (ch === " " || ch === "	" || ch === "\n" || ch === "\r") {
      state.index++;
    } else {
      break;
    }
  }
}
function readUntil(state, terminators) {
  const start = state.index;
  while (state.index < state.source.length) {
    const ch = state.source[state.index];
    if (terminators.includes(ch)) {
      break;
    }
    state.index++;
  }
  return state.source.slice(start, state.index);
}
function expect(state, ch) {
  if (state.source[state.index] !== ch) {
    throw new XoneCssError(
      `Se esperaba "${ch}" pero se encontro "${state.source[state.index] ?? "EOF"}".`,
      {
        code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
        source: state.source.slice(Math.max(0, state.index - 20), state.index + 20)
      }
    );
  }
  state.index++;
}
function parseDeclarationsBlock(state) {
  expect(state, "{");
  const declarations = [];
  let extendsFrom;
  while (state.index < state.source.length) {
    skipWhitespace(state);
    if (state.source[state.index] === "}") {
      state.index++;
      return { declarations, extendsFrom };
    }
    const property = readUntil(state, ":};").trim();
    if (property.length === 0) {
      if (state.source[state.index] === ";") {
        state.index++;
        continue;
      }
      throw new XoneCssError("Propiedad CSS vacia.", {
        code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
        source: state.source.slice(state.index, state.index + 40)
      });
    }
    if (state.source[state.index] !== ":") {
      throw new XoneCssError(
        `Se esperaba ":" tras la propiedad "${property}".`,
        {
          code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
          source: state.source.slice(
            Math.max(0, state.index - 20),
            state.index + 20
          )
        }
      );
    }
    state.index++;
    const value = readUntil(state, ";}").trim();
    if (state.source[state.index] === ";") {
      state.index++;
    }
    if (property.toLowerCase() === "extends") {
      extendsFrom = value;
    } else {
      declarations.push({ property, value });
    }
  }
  throw new XoneCssError('Bloque sin cerrar (falta "}").', {
    code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR
  });
}
function parseRule(state) {
  const selectorText = readUntil(state, "{").trim();
  if (selectorText.length === 0) {
    throw new XoneCssError("Selector vacio.", {
      code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
      source: state.source.slice(state.index, state.index + 40)
    });
  }
  const selector = parseSelector(selectorText);
  const { declarations, extendsFrom } = parseDeclarationsBlock(state);
  return {
    selector,
    rawSelector: selectorText,
    declarations,
    extendsFrom
  };
}
function parsePlatformBlock(state, opts) {
  const header = readUntil(state, "{").trim();
  const match = /^platform\s*:\s*([A-Za-z][A-Za-z0-9_-]*)$/.exec(header);
  if (!match) {
    throw new XoneCssError(
      `Cabecera @platform malformada: "@${header}".`,
      {
        code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
        source: header
      }
    );
  }
  const tag = match[1];
  assertValidPlatformTag(tag);
  expect(state, "{");
  const rules = [];
  while (state.index < state.source.length) {
    skipWhitespace(state);
    if (state.source[state.index] === "}") {
      state.index++;
      return { platform: tag, rules };
    }
    if (state.source[state.index] === "@") {
      throw new XoneCssError(
        "@platform no se puede anidar dentro de otro @platform.",
        {
          code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
          source: state.source.slice(state.index, state.index + 40)
        }
      );
    }
    const ruleStart = state.index;
    try {
      rules.push(parseRule(state));
    } catch (err) {
      if (opts.resilient !== true || !(err instanceof XoneCssError)) {
        throw err;
      }
      opts.onRuleError?.({
        source: err.source ?? state.source.slice(ruleStart, ruleStart + 60),
        message: err.message,
        code: err.code
      });
      skipBrokenRule(state, ruleStart);
    }
  }
  throw new XoneCssError("Bloque @platform sin cerrar.", {
    code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR
  });
}
function parseXoneCss(source, options = {}) {
  if (typeof source !== "string") {
    throw new XoneCssError("La fuente CSS debe ser una cadena.", {
      code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR
    });
  }
  const clean = stripComments(source);
  const state = { source: clean, index: 0 };
  const rules = [];
  const platformBlocks = [];
  while (state.index < state.source.length) {
    skipWhitespace(state);
    if (state.index >= state.source.length) {
      break;
    }
    const ch = state.source[state.index];
    if (ch === "@") {
      const blockStart = state.index;
      state.index++;
      try {
        platformBlocks.push(parsePlatformBlock(state, options));
      } catch (err) {
        if (options.resilient !== true || !(err instanceof XoneCssError)) {
          throw err;
        }
        options.onRuleError?.({
          source: err.source ?? state.source.slice(blockStart, blockStart + 60),
          message: err.message,
          code: err.code
        });
        skipBrokenRule(state, blockStart);
      }
      continue;
    }
    const ruleStart = state.index;
    try {
      rules.push(parseRule(state));
    } catch (err) {
      if (options.resilient !== true || !(err instanceof XoneCssError)) {
        throw err;
      }
      options.onRuleError?.({
        source: err.source ?? state.source.slice(ruleStart, ruleStart + 60),
        message: err.message,
        code: err.code
      });
      skipBrokenRule(state, ruleStart);
    }
  }
  return { rules, platformBlocks };
}

// src/extends.ts
function indexRules(rules) {
  const map = /* @__PURE__ */ new Map();
  for (const rule of rules) {
    const key = selectorKey(rule.selector);
    if (rule.selector.type === "compound") {
      for (const sub of rule.selector.selectors) {
        map.set(selectorKey(sub), rule);
      }
    } else {
      map.set(key, rule);
    }
  }
  return map;
}
function resolveRule(rule, index, visited, opts) {
  const key = selectorKey(rule.selector);
  if (visited.has(key)) {
    throw new XoneCssError(
      `Ciclo en extends detectado para "${rule.rawSelector}" (cadena: ${[...visited, key].join(" -> ")}).`,
      {
        code: XONE_CSS_ERROR_CODES.CIRCULAR_EXTENDS,
        source: rule.rawSelector
      }
    );
  }
  visited.add(key);
  const result = /* @__PURE__ */ new Map();
  if (rule.extendsFrom !== void 0) {
    const parentKey = rule.extendsFrom.trim();
    const parent = index.get(parentKey);
    if (!parent) {
      if (opts.lenient === true) {
        const msg = `[xone-css-engine] extends no resuelto: "${parentKey}" (referenciado desde "${rule.rawSelector}"). Se omite la herencia.`;
        if (opts.onWarning) opts.onWarning(msg);
        else console.warn(msg);
      } else {
        throw new XoneCssError(
          `extends no resuelto: "${parentKey}" (referenciado desde "${rule.rawSelector}").`,
          {
            code: XONE_CSS_ERROR_CODES.UNRESOLVED_EXTENDS,
            source: rule.rawSelector
          }
        );
      }
    } else {
      const parentDecls = resolveRule(parent, index, visited, opts);
      for (const [k, v] of parentDecls) {
        result.set(k, v);
      }
    }
  }
  for (const decl of rule.declarations) {
    result.set(decl.property, decl.value);
  }
  visited.delete(key);
  return result;
}
function resolveExtends(ast, options = {}) {
  const allRules = [...ast.rules];
  for (const block of ast.platformBlocks) {
    allRules.push(...block.rules);
  }
  const index = indexRules(allRules);
  const resolveAll = (rules) => rules.map((rule) => ({
    ...rule,
    resolvedDeclarations: resolveRule(rule, index, /* @__PURE__ */ new Set(), options)
  }));
  return {
    rules: resolveAll(ast.rules),
    platformBlocks: ast.platformBlocks.map((block) => ({
      platform: block.platform,
      rules: resolveAll(block.rules)
    }))
  };
}

// src/units.ts
var FORBIDDEN_UNITS = ["px", "em", "rem", "vh", "vw", "vmin", "vmax", "pt", "cm", "mm", "in"];
var XONE_FONTSIZE_OFFSET = 8;
var VALUE_TOKEN_RE = /(-?\d*\.?\d+)([a-zA-Z%]+)/g;
var FONT_SCALE_PROPS = /* @__PURE__ */ new Set([
  "fontsize",
  "text-fontsize",
  "label-fontsize",
  "labelfontsize",
  "textfontsize",
  "no-data-fontsize",
  "fontsize-legend"
]);
function transformUnits(value, contextProperty, options = {}) {
  if (typeof value !== "string") {
    return value;
  }
  const strict = options.strict !== false;
  const emitResponsive = options.emitResponsive === true;
  const isFontProp = contextProperty !== void 0 && FONT_SCALE_PROPS.has(contextProperty);
  const responsiveVar = isFontProp ? "var(--xone-font-unit, 1px)" : "var(--xone-p-unit, 1px)";
  return value.replace(VALUE_TOKEN_RE, (_match, num, unit) => {
    const lower = unit.toLowerCase();
    if (lower === "p") {
      return emitResponsive ? `calc(${num} * ${responsiveVar})` : `${num}px`;
    }
    if (lower === "%") {
      return `${num}%`;
    }
    if (FORBIDDEN_UNITS.includes(lower)) {
      if (strict) {
        throw new XoneCssError(
          `Unidad CSS prohibida en XOne: "${unit}". Usa "p" (puntos) o "%".`,
          {
            code: XONE_CSS_ERROR_CODES.INVALID_UNIT,
            source: contextProperty ? `${contextProperty}: ${value}` : value
          }
        );
      }
      if (options.onForbiddenUnit) {
        options.onForbiddenUnit({ unit, value });
      }
      return `${num}${unit}`;
    }
    return `${num}${unit}`;
  });
}

// src/colors.ts
var HEX_COLOR_RE = /(?<!#)#([0-9A-Fa-f]{8}|[0-9A-Fa-f]{6})\b(?!#)/g;
function transformColors(value, contextProperty) {
  if (typeof value !== "string") {
    return value;
  }
  value = value.replace(/^"(#[0-9A-Fa-f]+)"?$/, "$1").replace(/^"?(#[0-9A-Fa-f]+)"$/, "$1");
  const invalidHex = /(?<!#)#([0-9A-Fa-f]+)(?!#)/g;
  let m;
  while ((m = invalidHex.exec(value)) !== null) {
    const hex = m[1];
    if (hex.length !== 6 && hex.length !== 8) {
      throw new XoneCssError(
        `Color hex invalido "#${hex}". XOne acepta 6 (RRGGBB) u 8 (AARRGGBB) digitos.`,
        {
          code: XONE_CSS_ERROR_CODES.INVALID_COLOR,
          source: contextProperty ? `${contextProperty}: ${value}` : value
        }
      );
    }
  }
  return value.replace(HEX_COLOR_RE, (_match, hex) => {
    if (hex.length === 6) {
      return `#${hex.toUpperCase()}`;
    }
    const aa = hex.slice(0, 2);
    const rr = hex.slice(2, 4);
    const gg = hex.slice(4, 6);
    const bb = hex.slice(6, 8);
    return `#${(rr + gg + bb + aa).toUpperCase()}`;
  });
}

// src/transformer.ts
var PROPERTY_MAP = {
  bgcolor: "background-color",
  forecolor: "color",
  // `text-forecolor`/`text-bgcolor` son del SUBVIEW de texto (EditText), NO del
  // control entero: solo aplican a props editables de entrada de texto (T, N,
  // N#, X, D, DT). Se emiten como custom properties que CONSUME una regla del
  // baseline acotada a esos tipos (igual que `text-border-*`). Antes mapeaban a
  // `color`/`background-color` planos → un `cl_text_forecolor1` en un BOTÓN
  // pintaba la etiqueta del color del texto (p.ej. morado sobre morado en el
  // login de Alivia). El color del botón/label es `forecolor` (sin prefijo).
  "text-forecolor": "--xone-text-forecolor",
  "text-bgcolor": "--xone-text-bgcolor",
  // Dimensiones — son CSS estándar; si no se mapean, el catchall las
  // prefijaría como --xone-width/--xone-height rompiendo todo el sizing.
  width: "width",
  height: "height",
  "min-width": "min-width",
  "min-height": "min-height",
  "max-width": "max-width",
  "max-height": "max-height",
  fontsize: "font-size",
  "text-fontsize": "font-size",
  "label-fontsize": "font-size",
  labelfontsize: "font-size",
  textfontsize: "font-size",
  fontstyle: "font-style",
  fontname: "font-family",
  fontbold: "font-weight",
  fontitalic: "font-style",
  fontunderline: "text-decoration",
  "labelfont-underline": "text-decoration",
  "textfont-underline": "text-decoration",
  "text-align": "text-align",
  lmargin: "margin-left",
  rmargin: "margin-right",
  tmargin: "margin-top",
  bmargin: "margin-bottom",
  lpadding: "padding-left",
  rpadding: "padding-right",
  tpadding: "padding-top",
  bpadding: "padding-bottom",
  "border-corner-radius": "border-radius",
  "border-corner-radius-top-left": "border-top-left-radius",
  "border-corner-radius-top-right": "border-top-right-radius",
  "border-corner-radius-bottom-left": "border-bottom-left-radius",
  "border-corner-radius-bottom-right": "border-bottom-right-radius",
  "border-color": "border-color",
  "border-color-focus": "--xone-border-color-focus",
  "border-width": "border-width",
  "bordr-width-selected": "--xone-border-width-selected",
  "border-width-selected": "--xone-border-width-selected",
  "floating-tooltip-color": "--xone-floating-label-color",
  "expanded-hint-color": "--xone-floating-label-color",
  // Bordes del subview de TEXTO (text-border-*): solo aplican a props
  // editables de entrada de texto (T, N, N#, X, D, DT). Se emiten como custom
  // properties que CONSUME una regla del baseline acotada a esos tipos
  // (.xone-prop-t/-x/-n/-n1..6/-d/-dt). En el resto de props la var queda
  // inerte → no pinta borde (paridad XoneCSSTextBox: el text-border es del
  // EditText, no del frame). El borde del frame es `border-*` (sin prefijo).
  "text-border-color": "--xone-text-border-color",
  "text-border-width": "--xone-text-border-width",
  // Posicionamiento (solo significativos con floating/fixed)
  top: "top",
  left: "left",
  // Estado disabled/focus/pressed → custom properties consumidas
  // por pseudo-clases (:disabled, :focus, :active) en el baseline CSS
  "bgcolor-disabled": "--xone-bgcolor-disabled",
  "forecolor-disabled": "--xone-forecolor-disabled",
  "bgcolor-focus": "--xone-bgcolor-focus",
  "bgcolor-pressed": "--xone-bgcolor-pressed",
  "forecolor-pressed": "--xone-forecolor-pressed",
  "text-bgcolor-disabled": "--xone-text-bgcolor-disabled",
  "text-bgcolor-focus": "--xone-text-bgcolor-focus",
  "text-forecolor-disabled": "--xone-text-forecolor-disabled",
  // Fontsize para estado "sin datos"
  "no-data-fontsize": "--xone-no-data-fontsize",
  // Shadow color (combinado con elevation para box-shadow)
  "shadow-color": "--xone-shadow-color",
  // Chart legend
  "show-legend": "--xone-show-legend",
  "fontsize-legend": "--xone-fontsize-legend",
  // Checkbox/switch theming
  "check-color-checked": "--xone-check-color-checked",
  "check-color-unchecked": "--xone-check-color-unchecked",
  "check-color-checked-disabled": "--xone-check-color-checked-disabled",
  "check-color-unchecked-disabled": "--xone-check-color-unchecked-disabled",
  "track-color": "--xone-switch-track-color",
  "thumb-color": "--xone-switch-thumb-color"
};
var SIZE_PROPS = /* @__PURE__ */ new Set([
  "width",
  "height",
  "min-width",
  "min-height",
  "max-width",
  "max-height"
]);
var META_PROPS = /* @__PURE__ */ new Set([
  "notab",
  "show-toolbar",
  "group-swipe",
  "editmask",
  "labelbox",
  "order",
  "labelwidth",
  "locked",
  "locking",
  "dependent",
  "check-owner",
  "nomenmask",
  "show-selected-item",
  "selected-item-start-index",
  "apply-css",
  "fieldsize",
  "floating-tooltip",
  "zoom-controls",
  "fixed-lines",
  "show-counter",
  "title",
  "drawer-orientation",
  "viewmode",
  "gallery-columns",
  "orientation",
  "newline",
  "page-swipe",
  "mapview-embedded",
  "zoom-to-my-location",
  "show-pois",
  "clear-lines-on-refresh",
  "clear-markers-on-refresh",
  "show-compass",
  "show-minimap",
  "show-scale",
  "follow-location-on-background",
  "zoom-buttons-visibility",
  "show-google-buttons",
  "zoom-to-pois"
]);
var ANIMATION_TOKEN_MAP = {
  "##RIGHT_IN##": "xone-slideInRight",
  "##LEFT_IN##": "xone-slideInLeft",
  "##RIGHT_OUT##": "xone-slideOutRight",
  "##LEFT_OUT##": "xone-slideOutLeft",
  "##PUSH_IN##": "xone-slideInUp",
  "##PUSH_OUT##": "xone-slideOutUp",
  "##PUSH_DOWN_IN##": "xone-slideInDown",
  "##PUSH_DOWN_OUT##": "xone-slideOutDown",
  "##ALPHA_IN##": "xone-fadeIn",
  "##ALPHA_OUT##": "xone-fadeOut",
  "##ZOOM_IN##": "xone-zoomIn",
  "##ZOOM_OUT##": "xone-zoomOut",
  "##ROTATE3D_IN##": "xone-rotate3dIn",
  "##ROTATE3D_OUT##": "xone-rotate3dOut"
};
function normalizeResourceUrl(raw) {
  if (raw === "" || raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:")) return raw;
  if (raw.includes("##")) return raw;
  let n = raw.replace(/\\/g, "/");
  if (n.startsWith("./")) n = n.slice(2);
  if (n.startsWith("/")) return n;
  if (n.includes("/")) return `/${n}`;
  return `/icons/${n}`;
}
function normalizeFontFamily(raw) {
  const v = raw.trim();
  if (!v || v.includes(",")) return v;
  const stripped = v.replace(/\.(ttf|otf|woff2?)$/i, "");
  if (/^['"].*['"]$/.test(stripped)) return stripped;
  if (!/^[A-Za-z_][A-Za-z0-9_-]*$/.test(stripped)) {
    return `'${stripped.replace(/'/g, "\\'")}'`;
  }
  return stripped;
}
function mapValue(property, rawValue, unitsOpts = { strict: false }, onUnmappedProperty) {
  if (property === "visible") {
    if (rawValue.trim() === "0") {
      return { property: "display", value: "none" };
    }
    return null;
  }
  if (property === "text-border") {
    const v = rawValue.trim().toLowerCase();
    const value2 = v === "false" ? "none" : "solid";
    return [
      { property: "--xone-text-border-top", value: value2 },
      { property: "--xone-text-border-right", value: value2 },
      { property: "--xone-text-border-bottom", value: value2 },
      { property: "--xone-text-border-left", value: value2 },
      { property: "--xone-text-border", value: value2 }
    ];
  }
  if (property === "border" && rawValue.trim().toLowerCase() === "false") {
    return { property: "border", value: "none" };
  }
  if (/^border-(top|bottom|left|right)$/.test(property) && rawValue.trim().toLowerCase() === "false") {
    return { property: `${property}`, value: "none" };
  }
  if (property === "scroll") {
    if (rawValue.trim().toLowerCase() === "true") {
      return { property: "overflow-y", value: "auto; overflow-x: hidden" };
    }
    return null;
  }
  if (META_PROPS.has(property)) return null;
  if (property === "fontbold" || property === "textfont-bold" || property === "label-fontbold" || property === "labelfont-bold") {
    const v = rawValue.trim().toLowerCase();
    return { property: "font-weight", value: v === "true" ? "bold" : "normal" };
  }
  if (property === "fontitalic" || property === "textfont-italic" || property === "label-fontitalic") {
    const v = rawValue.trim().toLowerCase();
    return { property: "font-style", value: v === "true" ? "italic" : "normal" };
  }
  if (property === "fontunderline" || property === "labelfont-underline" || property === "textfont-underline") {
    const v = rawValue.trim().toLowerCase();
    return { property: "text-decoration", value: v === "true" ? "underline" : "none" };
  }
  if (property === "framebox") {
    const v = rawValue.trim().toLowerCase();
    if (v === "false") return { property: "border", value: "none" };
    return null;
  }
  if (property === "align") {
    return { property: "--xone-align", value: rawValue.trim() };
  }
  if (property === "text-align" && rawValue.includes("|")) {
    const tokens = rawValue.trim().toLowerCase().split("|").map((t) => t.trim());
    const h = tokens.find((t) => t === "left" || t === "center" || t === "right" || t === "justify");
    if (h) return { property: "text-align", value: h };
    return null;
  }
  if (/^text-border-(top|bottom|left|right)$/.test(property)) {
    const side = property.replace("text-border-", "");
    const v = rawValue.trim().toLowerCase();
    if (v === "false") return { property: `--xone-text-border-${side}`, value: "none" };
    if (v === "true") return { property: `--xone-text-border-${side}`, value: "solid" };
    return null;
  }
  if (property === "elevation") {
    const e = parseFloat(rawValue.trim());
    if (!Number.isFinite(e)) return null;
    if (e <= 0) return { property: "box-shadow", value: "none" };
    const clamped = Math.min(e, 24);
    const keyY = Math.round(clamped * 0.5 + 1);
    const keyBlur = Math.round(clamped * 1.5 + 1);
    const keyAlpha = Math.min(0.1 + clamped * 5e-3, 0.3).toFixed(2);
    const ambBlur = Math.round(clamped * 1 + 2);
    const ambSpread = Math.round(clamped * 0.25);
    const ambAlpha = Math.min(0.08 + clamped * 4e-3, 0.22).toFixed(2);
    const responsive2 = unitsOpts.emitResponsive === true;
    const px = (n) => responsive2 ? `calc(${n} * var(--xone-p-unit, 1px))` : `${n}px`;
    return {
      property: "box-shadow",
      value: `0 ${px(keyY)} ${px(keyBlur)} rgba(0,0,0,${keyAlpha}), 0 ${px(ambSpread)} ${px(ambBlur)} rgba(0,0,0,${ambAlpha})`
    };
  }
  if (property === "label-wrap") {
    return {
      property: "white-space",
      value: rawValue.trim().toLowerCase() === "true" ? "normal" : "nowrap"
    };
  }
  if (property === "lines") {
    const n = parseInt(rawValue.trim(), 10);
    if (Number.isFinite(n) && n > 0) return { property: "--xone-lines", value: String(n) };
    return null;
  }
  if (property === "labelshadow") {
    if (rawValue.trim().toLowerCase() === "true") {
      return { property: "text-shadow", value: "1px 1px 2px rgba(0, 0, 0, 0.3)" };
    }
    return null;
  }
  if (property === "floating") {
    if (rawValue.trim().toLowerCase() === "true") {
      return { property: "position", value: "absolute" };
    }
    return null;
  }
  if (property === "fixed") {
    if (rawValue.trim().toLowerCase() === "true") {
      return { property: "position", value: "fixed" };
    }
    return null;
  }
  if (property === "ripple-effect") {
    if (rawValue.trim().toLowerCase() === "true") {
      return { property: "--xone-ripple", value: "1" };
    }
    return null;
  }
  if (property === "imgbk") {
    const v = rawValue.trim();
    if (v.length === 0) return null;
    const url = normalizeResourceUrl(v);
    const safe = url.replace(/"/g, '\\"');
    return {
      property: "background-image",
      value: `url("${safe}"); background-size: cover; background-position: center; background-repeat: no-repeat`
    };
  }
  if (property === "animation-in" || property === "animation-out") {
    const token = rawValue.trim();
    const name = ANIMATION_TOKEN_MAP[token];
    if (name) return { property: `--xone-${property}`, value: name };
    return null;
  }
  if (property === "animation-in-delay" || property === "animation-out-delay") {
    const ms = rawValue.trim();
    return { property: `--xone-${property}`, value: /^\d+$/.test(ms) ? `${ms}ms` : ms };
  }
  if ((property === "bgcolor" || property === "text-bgcolor") && rawValue.includes(",")) {
    const colors = rawValue.split(",").map((c) => transformColors(c.trim(), property));
    return {
      property: "background",
      value: `linear-gradient(to right, ${colors.join(", ")})`
    };
  }
  if (property.startsWith("cell-")) {
    let v = transformColors(rawValue, property);
    v = transformUnits(v, property, unitsOpts);
    return { property: `--xone-${property}`, value: v };
  }
  if (property.startsWith("tab-")) {
    let v = transformColors(rawValue, property);
    v = transformUnits(v, property, unitsOpts);
    return { property: `--xone-${property}`, value: v };
  }
  if (property.startsWith("chart-")) {
    let v = transformColors(rawValue, property);
    return { property: `--xone-${property}`, value: v };
  }
  if (property.startsWith("img-")) {
    return { property: `--xone-${property}`, value: rawValue.trim() };
  }
  if (property.startsWith("weekdays-")) {
    let v = transformColors(rawValue, property);
    v = transformUnits(v, property, unitsOpts);
    return { property: `--xone-${property}`, value: v };
  }
  if (property.startsWith("tooltip-")) {
    let v = transformColors(rawValue, property);
    return { property: `--xone-${property}`, value: v };
  }
  const XONE_GRID_PROPS = /* @__PURE__ */ new Set([
    "grid-framebox",
    "grid-text-border",
    "grid-text-bgcolor",
    "grid-bgcolor",
    "grid-forecolor",
    "grid-fontsize"
  ]);
  if (XONE_GRID_PROPS.has(property)) {
    let v = transformColors(rawValue, property);
    v = transformUnits(v, property, unitsOpts);
    return { property: `--xone-${property}`, value: v };
  }
  if (SIZE_PROPS.has(property)) {
    const t = rawValue.trim();
    if (/^-\d+$/.test(t)) {
      const mapped = PROPERTY_MAP[property];
      return { property: mapped, value: Number(t) === -1 ? "100%" : "auto" };
    }
  }
  const mappedProp = PROPERTY_MAP[property] ?? null;
  let value = rawValue;
  value = transformColors(value, property);
  value = transformUnits(value, property, unitsOpts);
  const responsive = unitsOpts.emitResponsive === true;
  const fontProps = property === "fontsize" || property === "text-fontsize" || property === "label-fontsize" || property === "labelfontsize" || property === "textfontsize" || property === "no-data-fontsize" || property === "fontsize-legend";
  if (fontProps) {
    const trimmed = value.trim();
    let n = null;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      n = Number(trimmed);
    } else {
      const m = trimmed.match(
        /^calc\(\s*(-?\d+(?:\.\d+)?)\s*\*\s*var\(--xone-(?:p|font)-unit,\s*1px\)\s*\)$/
      );
      if (m) n = Number(m[1]);
    }
    if (n !== null && Number.isFinite(n)) {
      const offset = unitsOpts.fontscaleOffset ?? XONE_FONTSIZE_OFFSET;
      value = responsive ? `calc((${offset} + ${n}) * var(--xone-font-unit, 1px))` : `${n + offset}px`;
    }
  }
  const pProps = property === "border-corner-radius" || property === "border-corner-radius-top-left" || property === "border-corner-radius-top-right" || property === "border-corner-radius-bottom-left" || property === "border-corner-radius-bottom-right" || property === "border-width" || property === "text-border-width" || property === "bordr-width-selected" || property === "border-width-selected";
  if (pProps && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    const n = value.trim();
    value = responsive ? `calc(${n} * var(--xone-p-unit, 1px))` : `${n}px`;
  }
  if (property === "fontname" || property === "font-family") {
    value = normalizeFontFamily(value);
  }
  if (mappedProp === null) {
    onUnmappedProperty?.({ property, value: rawValue });
    const safeName = property.replace(/[^a-zA-Z0-9-]/g, "-");
    return { property: `--xone-${safeName}`, value };
  }
  return { property: mappedProp, value };
}
function emitRule(rule, pretty, indent, unitsOpts, onUnknownSelector, onUnmappedProperty) {
  if (rule.selector.type === "unknown") {
    onUnknownSelector?.({ name: rule.selector.name, source: rule.rawSelector });
    return null;
  }
  if (rule.selector.type === "compound") {
    for (const sub of rule.selector.selectors) {
      if (sub.type === "unknown") {
        onUnknownSelector?.({ name: sub.name, source: rule.rawSelector });
      }
    }
  }
  const selector = selectorToCss(rule.selector);
  if (selector.length === 0) {
    return null;
  }
  const declMap = /* @__PURE__ */ new Map();
  let hasUniformRadius = false;
  let hasFrameboxTrue = false;
  for (const [prop, value] of rule.resolvedDeclarations) {
    if (prop === "framebox" && value.trim().toLowerCase() === "true") {
      hasFrameboxTrue = true;
    }
    const mapped = mapValue(prop, value, unitsOpts, onUnmappedProperty);
    if (!mapped) continue;
    for (const m of Array.isArray(mapped) ? mapped : [mapped]) {
      declMap.set(m.property, m.value);
    }
    if (prop === "border-corner-radius") hasUniformRadius = true;
  }
  if (hasFrameboxTrue && declMap.has("border-color") && !declMap.has("border-width") && !declMap.has("border-style") && !declMap.has("border")) {
    declMap.set("border-width", "1px");
    declMap.set("border-style", "solid");
  }
  const bw = declMap.get("border-width");
  if (bw && !declMap.has("border-style") && !declMap.has("border") && parseFloat(bw) > 0) {
    declMap.set("border-style", "solid");
  }
  const overflowRules = [];
  if (hasUniformRadius) {
    const frameSel = scopeSelector(selector, ".xone-frame", pretty);
    const propSel = scopeSelector(selector, ".xone-prop", pretty);
    if (pretty) {
      overflowRules.push(
        `${indent}${frameSel} {
${indent}    overflow-y: auto;
${indent}    overflow-x: hidden;
${indent}}`
      );
      overflowRules.push(
        `${indent}${propSel} {
${indent}    overflow: hidden;
${indent}}`
      );
    } else {
      overflowRules.push(`${frameSel}{overflow-y:auto;overflow-x:hidden}`);
      overflowRules.push(`${propSel}{overflow:hidden}`);
    }
  }
  if (declMap.size === 0) {
    return overflowRules.length > 0 ? overflowRules.join(pretty ? "\n" : "") : null;
  }
  const lines = [];
  for (const [prop, value] of declMap) {
    lines.push(`${indent}    ${prop}: ${value};`);
  }
  const baseRule = pretty ? `${indent}${selector} {
${lines.join("\n")}
${indent}}` : `${selector}{${lines.map((l) => l.trim()).join("")}}`;
  if (overflowRules.length === 0) {
    return baseRule;
  }
  return [baseRule, ...overflowRules].join(pretty ? "\n" : "");
}
function scopeSelector(selector, scopeClass, pretty) {
  const joiner = pretty ? ", " : ",";
  return selector.split(",").map((part) => `${part.trim()}${scopeClass}`).join(joiner);
}
function compileAst(resolved, options) {
  const pretty = options.pretty !== false;
  const strategy = options.platformStrategy ?? "media-query";
  const target = options.target;
  const unitsOpts = {
    strict: options.strictUnits === true,
    emitResponsive: options.emitResponsive === true,
    fontscaleOffset: options.fontscaleOffset ?? 8,
    ...options.onForbiddenUnit !== void 0 ? { onForbiddenUnit: options.onForbiddenUnit } : {}
  };
  const onUnknownSelector = options.onUnknownSelector;
  const onUnmappedProperty = options.onUnmappedProperty;
  const resilient = options.resilient === true;
  const out = [];
  const safeEmit = (rule, indent) => {
    if (!resilient) {
      return emitRule(rule, pretty, indent, unitsOpts, onUnknownSelector, onUnmappedProperty);
    }
    try {
      return emitRule(rule, pretty, indent, unitsOpts, onUnknownSelector, onUnmappedProperty);
    } catch (err) {
      const xerr = err instanceof XoneCssError ? err : void 0;
      options.onRuleError?.({
        source: rule.rawSelector,
        message: err instanceof Error ? err.message : String(err),
        code: xerr?.code ?? "XONE.CSS.SYNTAX_ERROR"
      });
      return null;
    }
  };
  for (const rule of resolved.rules) {
    const css = safeEmit(rule, "");
    if (css) out.push(css);
  }
  for (const block of resolved.platformBlocks) {
    const tag = block.platform;
    if (!matchesPlatform(tag, target)) {
      continue;
    }
    if (strategy === "filter") {
      for (const rule of block.rules) {
        const css = safeEmit(rule, "");
        if (css) out.push(css);
      }
      continue;
    }
    const mq = platformTagToMediaQuery(tag);
    if (mq === null) {
      if (target?.platform === tag) {
        for (const rule of block.rules) {
          const css = safeEmit(rule, "");
          if (css) out.push(css);
        }
      }
      continue;
    }
    const inner = [];
    for (const rule of block.rules) {
      const css = safeEmit(rule, "    ");
      if (css) inner.push(css);
    }
    if (inner.length === 0) continue;
    if (pretty) {
      out.push(`@media ${mq} {
${inner.join("\n")}
}`);
    } else {
      out.push(`@media ${mq}{${inner.map((l) => l.replace(/^\s+/, "")).join("")}}`);
    }
  }
  return pretty ? out.join("\n\n") : out.join("");
}
function transformToStandardCss(xoneSource, options = {}) {
  try {
    const ast = parseXoneCss(xoneSource, {
      resilient: options.resilient === true,
      ...options.onRuleError !== void 0 ? { onRuleError: options.onRuleError } : {}
    });
    const lenient = options.lenientExtends !== false;
    const resolved = resolveExtends(ast, {
      lenient,
      ...options.onUnresolvedExtends !== void 0 ? { onWarning: options.onUnresolvedExtends } : {}
    });
    return compileAst(resolved, options);
  } catch (err) {
    if (err instanceof XoneCssError) {
      throw err;
    }
    throw new XoneCssError(
      err instanceof Error ? err.message : "Error desconocido al transformar CSS XOne.",
      {
        code: "XONE.CSS.SYNTAX_ERROR",
        cause: err instanceof Error ? err : void 0
      }
    );
  }
}

// src/baseline/layoutPrimitives.ts
var LAYOUT_PRIMITIVES_CSS = String.raw`
/* El atributo ` + `\`hidden\` debe ganar SIEMPRE: el orchestrator lo togglea para
   \`disablevisible\` (paridad Android ControlsUtils.isPropertyVisible) y para
   grupos/tabs. Sin \`!important\`, \`.xone-frame { display: flex }\` (misma
   especificidad/capa) o \`.xone-prop { display: flex }\` pisan el \`display:none\`
   del UA y el elemento "oculto" sigue visible (bug modales flotantes Alivia). */
[hidden] { display: none !important; }

/* Overlay flotante "pass-through": un frame floating con fondo TOTALMENTE
   transparente (bgcolor alpha 00, p.ej. frmChatBot/frmBot cuando su contenido
   esta vacio/comentado o el iframe lo bloquea la CSP) es una caja invisible que,
   por defecto, seguiria interceptando los clicks sobre los controles de debajo
   (tapaba el boton "Volver" de las fichas: el back no funcionaba con el chatbot
   abierto). Lo marcamos data-xone-passthrough (frameRenderer, solo floating +
   alpha 00): su caja y sus contenedores vacios NO capturan puntero; el
   contenido interactivo real (islas/iframe/inputs/botones/enlaces) re-activa
   pointer-events, asi un panel transparente CON contenido sigue siendo usable. */
[data-xone-passthrough] { pointer-events: none; }
[data-xone-passthrough] xone-island,
[data-xone-passthrough] iframe,
[data-xone-passthrough] input,
[data-xone-passthrough] textarea,
[data-xone-passthrough] select,
[data-xone-passthrough] button,
[data-xone-passthrough] a,
[data-xone-passthrough] .xone-widget-host { pointer-events: auto; }

/* \u2500\u2500 Canvas \u2014 superficie ra\xEDz XOne (sistema responsive) \u2500\u2500
   container-type: size (no inline-size) expone cqw (consumido por
   --xone-p-unit en responsive) y cqh. El cqh es la referencia de ALTURA ra\xEDz
   (= la pantalla): un frame de nivel superior con height N% emite Ncqh y
   resuelve contra el alto del canvas (paridad ContentFramePage.java:344-345). */
.xone-canvas {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex: 1 1 auto;
    /* ALTURA DEFINIDA = la pantalla (paridad Android: el root es el screen).
       Debe ser height (no solo min-height): las cascadas height:N% de los
       frames de nivel superior necesitan un alto de padre DEFINIDO para
       resolver \u2014 min-height NO cuenta como definido para % en CSS, as\xED que
       sin esto toda la cadena height:100% colapsaba a la altura del contenido
       (bug paridad login/inicio 2026-05-30). dvh es absoluto (relativo al
       viewport), as\xED que es definido sin depender de <body>. El scroll de
       contenido que desborda va en frames con scroll="true", igual que Android. */
    height: 100dvh;
    min-height: 100dvh;
    width: 100%;
    /* RECORTE A PANTALLA (paridad Android): el View system recorta los hijos a
       los bounds del padre (clipChildren default) y la pantalla NUNCA crece \u2014
       el contenido que excede solo scrollea dentro de frames scroll="true"
       (ContentFramePage.java:344-353 dimensiona el root frame a
       nParentWidth/nParentHeight de la pantalla). Sin esto, un \xE1rbol cuyo
       dise\xF1o suma >100dvh (p.ej. Inicio mRed: header 8cqh + frmGeneral2 98cqh
       + m\xE1rgenes) extiende el scrollHeight del body (1027px vs 900 de la
       referencia). */
    overflow: clip;
    container-type: size;
    container-name: xoneCanvas;
}

/* Modo responsive base-FIJA (data-responsive="1" = web-resolution-width > 0).
   Tokens responsive: 1p = (1/base-width)*100cqw; 1 fontsize = font-factor * idem.
   La base la hornea el generador en --xone-base-width.

   Modo responsive base-AUTO (data-responsive="auto" = sin resoluci\xF3n declarada):
   NO se declara --xone-p-unit aqu\xED (la base es desconocida pre-JS). El
   canvasSizer mide el tama\xF1o natural del contenido, fija --xone-base-width y
   sobrescribe --xone-p-unit/--xone-font-unit inline. Sin JS, var(...,1px) cae a
   1px = 1p=1px = tama\xF1o natural del dise\xF1o (sin zoom). */
.xone-canvas[data-responsive="1"] {
    min-height: 100dvh;
    --xone-p-unit: calc(100cqw / var(--xone-base-width));
    --xone-font-unit: calc(var(--xone-font-factor) * 100cqw / var(--xone-base-width));
}
/* Modos de canvas y alineaci\xF3n: aplican a ambos modos responsive (la base
   --xone-base-width que necesitan fixed/letterbox la hornea el generador en
   "1" y la fija el canvasSizer tras medir en "auto"). */
.xone-canvas:is([data-responsive="1"],[data-responsive="auto"])[data-canvas-mode="fluid"] {
    width: min(100vw, var(--xone-canvas-max-width, 540px));
    align-self: center;
}
.xone-canvas:is([data-responsive="1"],[data-responsive="auto"])[data-canvas-mode="viewport"] {
    width: 100vw;
    max-width: none;
    align-self: stretch;
}
.xone-canvas:is([data-responsive="1"],[data-responsive="auto"])[data-canvas-mode="fixed"] {
    width: calc(var(--xone-base-width) * 1px);
    height: calc(var(--xone-base-height) * 1px);
    flex: 0 0 auto;
    min-height: 0;
    align-self: center;
}
.xone-canvas:is([data-responsive="1"],[data-responsive="auto"])[data-canvas-mode="letterbox"] {
    aspect-ratio: var(--xone-base-width) / var(--xone-base-height);
    width: min(100vw, calc(100dvh * var(--xone-base-width) / var(--xone-base-height)));
    /* height:auto deja que aspect-ratio gobierne el alto (la base ahora fija
       height:100dvh, que romper\xEDa la relaci\xF3n de aspecto si no se neutraliza). */
    height: auto;
    flex: 0 0 auto;
    min-height: 0;
    align-self: center;
}
.xone-canvas:is([data-responsive="1"],[data-responsive="auto"])[data-canvas-align="left"]   { align-self: flex-start; }
.xone-canvas:is([data-responsive="1"],[data-responsive="auto"])[data-canvas-align="right"]  { align-self: flex-end; }
.xone-canvas:is([data-responsive="1"],[data-responsive="auto"])[data-canvas-align="center"] { align-self: center; }

/* \u2500\u2500 Coll / Group / Frame: flex column por defecto \u2500\u2500 */
.xone-coll, .xone-group, .xone-frame {
    display: flex;
    flex-direction: column;
    align-items: stretch;
}
/* La coll = la pantalla (no puede crecer m\xE1s all\xE1 del canvas): height definido
   en vez de min-height para que la p\xE1gina mida exactamente la pantalla y el
   scroll viva en los frames scroll="true" (overflow:auto emitido por el
   lowering, visualStyle.ts:332-333 / applyVisualAttributes \xA71b), igual que en
   Android, donde nada se pinta fuera de pantalla. min-height:100% (anterior)
   dejaba crecer la coll y extend\xEDa el body (divergencia docHeight 1027 vs 900). */
.xone-coll { flex: 1 1 auto; height: 100%; min-height: 0; }
/* Group ra\xEDz = MATCH_PARENT (paridad EditGroupView.java:104). Los groups
   inactivos (tabs) van con [hidden]/display:none y no consumen flex. */
.xone-coll > .xone-group { flex: 1 1 auto; min-height: 0; }
/* position: relative para floating labels / overlays anclados al frame.
   min-width: 0 (espejo de min-height:0): un frame es un flex item cuyo minimum
   automatico (min-width:auto = min-content) le impedia encogerse por debajo del
   ancho intrinseco de su contenido. En una cadena de frames anidados con width
   en porcentaje y texto ENVOLVIBLE largo (WEAL-143: burbujas de chat de
   Mensajeria, MAP_MENSAJE con width 96 -> 90 -> 89 por ciento anidados), ese
   suelo de min-content propagaba el max-content hacia arriba y el arbol entero
   se dimensionaba por el texto SIN envolver (2483px en un panel de 699px ->
   scroll horizontal). Con min-width:0 la cadena puede encogerse a su ancho real
   del panel y el texto envuelve, igual que min-height:0 ya permite en el eje
   vertical. Paridad Android: un LinearLayout con hijos MATCH/porcentuales nunca
   crece mas que su padre (recorta/envuelve). */
.xone-frame { min-height: 0; min-width: 0; position: relative; }
/* WEAL-98 \u2014 paridad Android clipChildren=true en contenedores EXACTLY. Un frame
   con altura literal (Np/N% distinta de 100%; el lowering/runtime le a\xF1ade la
   clase xone-exactly-clip junto al container-type:size) nunca crece con su
   contenido: Android recorta en dispatchDraw lo que exceda la caja, as\xED que el
   default web correcto es clip (no visible, que dejaba al contenido sangrar
   sobre las filas vecinas de un listado \u2014 MensajeriaChatListado frmContent 60p).
   :where = especificidad 0: cualquier clase del proyecto (p.ej. .frmCuerpo con
   overflow-y:auto, WEAL-96) o estilo inline (scroll="true", radius) gana a este
   default. clip y no hidden para no crear un scroll container (un focus interno
   no puede desplazar el contenido del frame). */
:where(.xone-frame.xone-exactly-clip) { overflow: clip; }

/* \u2500\u2500 Row wrapper: agrupa hijos consecutivos con newline="false" \u2500\u2500 */
.xone-row {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    align-self: stretch;
    width: 100%;
    gap: 0;
    flex: 0 0 auto;
}
/* La fila \xFAnica de un group crece a su contenido (can\xF3nico: 1 1 auto). */
.xone-group > .xone-row { flex: 1 1 auto; }
/* La row \xFAnica de un frame con alto expl\xEDcito ocupa todo ese alto. */
.xone-frame[style*="height"] > .xone-row:only-child { flex: 1 1 auto; }
/* \u2026salvo que el frame declare un align vertical: entonces el bloque de l\xEDneas
   NO debe estirarse a todo el alto, sino conservar su tama\xF1o para que el
   posicionamiento vertical (top/center/bottom) lo coloque dentro del alto
   sobrante. Paridad Android EditFramePage.java:936 (vMainView.setGravity(sAlign)
   sobre LinearLayout VERTICAL: posiciona el bloque de l\xEDneas, no lo estira).
   P0-B: se pasa del fr\xE1gil [style*="justify-content"]:only-child (que fallaba
   con varias filas y depend\xEDa del substring inline) a [data-xone-align*=...]
   emitido por frameRenderer, v\xE1lido con N filas. */
.xone-frame[data-xone-align*="top"]    > .xone-row,
.xone-frame[data-xone-align*="bottom"] > .xone-row,
.xone-frame[data-xone-align*="center"] > .xone-row { flex: 0 0 auto; }
/* \u2026EXCEPCI\xD3N: si el \xFAnico contenido de la fila es una imagen full-bleed
   (cover/fill = imagen de fondo, p.ej. el fondo del login con width:100%
   height:100%), esa imagen es MATCH_PARENT y DEBE llenar el alto ignorando la
   gravity (en Android un hijo MATCH_PARENT llena; la gravity solo posiciona
   hijos WRAP_CONTENT). La fila vuelve a 1 1 auto para que height:100% de la
   imagen resuelva al alto del frame. Las im\xE1genes contain (keep-aspect, p.ej.
   un logo) NO entran aqu\xED \u2192 siguen centradas por gravity. */
.xone-frame[data-xone-align] > .xone-row:has(> img[style*="object-fit: fill"]:only-child),
.xone-frame[data-xone-align] > .xone-row:has(> img[style*="object-fit: cover"]:only-child) { flex: 1 1 auto; }
/* Frame de alto completo dentro de una fila horizontal: el ALTO lo da la
   resoluci\xF3n height:100% (ahora que el canvas tiene alto definido) o el
   align-items:stretch de la fila; el ANCHO lo da su atributo width (40%/60%\u2026).
   ANTES esto era flex:1 1 0, que en una fila reparte el eje principal
   (ANCHO) a partes iguales con basis 0 \u2192 aplastaba el width% (p.ej. 40/60 se
   volv\xEDa 50/50). flex:0 0 auto respeta el width inline (bug 2026-05-30). */
.xone-row > .xone-frame[style*="height: 100%"] { flex: 0 0 auto; }

/* \u2500\u2500 Prop: bloque vertical (stack), ocupa ancho del padre por defecto \u2500\u2500 */
.xone-prop { display: flex; flex-direction: column; width: 100%; min-height: 0; }
.xone-prop-tl { display: block; width: 100%; }

/* \u2500\u2500 Pesta\xF1as de grupos (F2): modelo "un grupo visible" \u2500\u2500
   El chasis (un <xone-island>) es flex column: [barra 0 0 auto][grupos 1 1 auto].
   La barra se omite en SSR si hay <2 grupos normales o notab (R1). El JS solo
   conmuta hidden/aria-selected; el layout y el primer grupo funcionan sin JS. */
.xone-tabs-chassis { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }
.xone-tabs {
    display: flex; flex-direction: row; flex: 0 0 auto;
    overflow-x: auto; scrollbar-width: thin;
    background: var(--xone-tab-bg, transparent);
}
.xone-tabs[data-tab-orientation="bottom"] { order: 2; }
.xone-tab {
    /* FIX-6: WRAP_CONTENT empaquetado a la izquierda \u2014 un prop/bot\xF3n sin
       width jam\xE1s recibe MATCH_PARENT ni reparto impl\xEDcito (paridad
       EditFramePage.java:1042: lp WRAP_CONTENT; misma regla que im\xE1genes
       XOneImageView.java:271-281). Antes: flex 1 1 0 (reparto equitativo
       no can\xF3nico que estiraba cada tab a 1/N del ancho). */
    flex: 0 0 auto;
    min-height: var(--xone-tab-height, auto);
    font-size: var(--xone-tab-fontsize, inherit);
    background: transparent;
    color: var(--xone-tab-fg, inherit);
    border: 0; border-bottom: 2px solid transparent;
    padding: 0.6em 1em; cursor: pointer; white-space: nowrap;
    font-family: inherit;
}
/* Si el proyecto declara tab-width, las pesta\xF1as pasan a ancho fijo + scroll. */
.xone-tabs[style*="--xone-tab-width"] .xone-tab { flex: 0 0 var(--xone-tab-width); }
.xone-tab[aria-selected="true"] {
    color: var(--xone-tab-fg-selected, var(--xone-tab-fg, inherit));
    border-bottom-color: var(--xone-tab-indicator, currentColor);
}
.xone-tab:focus-visible { outline: 2px solid var(--xone-tab-indicator, currentColor); outline-offset: -2px; }
.xone-groups { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }
.xone-groups > .xone-group { flex: 1 1 auto; min-height: 0; }
/* WMRED-9: con barra de pesta\xF1as NATIVA, el \xE1rea de grupos es la referencia de
   ALTURA de los frames de pesta\xF1a (paridad Android: la p\xE1gina del ViewPager mide
   "contenedor \u2212 tira de pesta\xF1as" y sus hijos con height=N% resuelven contra esa
   p\xE1gina). Sin esto, un frame de pesta\xF1a con height=N% (\u2260100%, que emite Ncqh)
   rebasaba al size container ANTERIOR a la barra \u2014el frame que aloja el coll o
   el canvas\u2014 y se sal\xEDa por abajo exactamente N% \xD7 alto de la barra (Inicio de
   mRED: frame scroll="true" 98% acababa 17 px fuera de la ventana y el \xFAltimo
   panel nunca mostraba su borde inferior). Mismo patr\xF3n que .xone-coll-center
   (WEAL-30) para las bandas fixed. Solo se aplica si la barra existe en el DOM
   (sin barra el \xE1rea de grupos ya coincide con el chasis: nada que rebasar). El
   chasis siempre tiene alto definido (.xone-coll height:100% + flex), as\xED que la
   size-containment no colapsa. */
.xone-tabs-chassis > .xone-tabs ~ .xone-groups { container-type: size; }
.xone-group[hidden] { display: none; }

/* \u2500\u2500 Grupos fijos (F3): bandas ancladas que restan espacio al centro \u2500\u2500
   El chasis es flex: las bandas (flex:0 0 auto) miden su contenido (WRAP) y el
   centro (flex:1 1 auto) ocupa el resto. Reproduce "pantalla \u2212 fijos \u2212 tabs" sin
   position:fixed. Banda oculta (disablevisible) \u2192 0 espacio. */
.xone-coll-mid { display: flex; flex-direction: row; flex: 1 1 auto; min-height: 0; }
/* El centro NO-fijo del chasis de grupos fijos. container-type:size lo vuelve
   la referencia de ALTURA de los frames de pesta\xF1a: un frame ra\xEDz con height=N%
   emite Ncqh (pctHeightToCqh) y resuelve contra el ALTO RESTANTE del centro
   (pantalla \u2212 bandas fixed), no contra el coll completo (que incluye la banda
   fixed). Replica el "\xE1rea restante" de Android (EditViewHyper.mapFixedGroupViews:
   los fixed miden contra el contenedor completo y el contenido no-fixed recibe
   el sobrante). Sin esto, 90cqh med\xEDa contra el coll y desbordaba el viewport
   por exactamente la altura de la banda fixed x N% (WEAL-30). Seguro para el
   escalado responsive: canvasSizer fija --xone-p-unit como px sobre el canvas
   (no cqw), igual que ya hacen .xone-canvas y los frames containerizados. */
.xone-coll-center { display: flex; flex-direction: column; flex: 1 1 auto; min-width: 0; min-height: 0; container-type: size; }
/* WEAL-418 \u2014 un \xDANICO grupo no-fijo cuelga DIRECTO del centro (sin chasis de
   pesta\xF1as, collectionRenderer.renderCenter rama !useTabs). Sin esta regla el
   section quedaba en flex:0 1 auto + min-height:auto y crec\xEDa con su contenido
   m\xE1s all\xE1 del centro \u2192 el frame scroll="true" nunca desbordaba (clientHeight ==
   scrollHeight) y lo de abajo quedaba recortado. Paridad Android: un grupo no
   fijo es MATCH_PARENT del \xC1REA RESTANTE (EditGroupView.java:104,
   EditViewHyper.mapFixedGroupViews) \u2014 mismo criterio que .xone-coll > .xone-group
   y .xone-groups > .xone-group. Cubre tambi\xE9n .xone-coll-mid > .xone-coll-center. */
.xone-coll-center > .xone-group { flex: 1 1 auto; min-height: 0; }
/* [WLDA-F7] P\xE1ginas con web-layout: htmlLowering renderiza el layout YA aplicado y lo envuelve en el canvas SIN
   article.xone-coll (renderCanvas(applyLayout(...))), as\xED que el section.xone-group del layout cuelga
   directamente de main.xone-canvas y no le llegaba la regla de arriba: quedaba en flex:0 1 auto; min-height:auto,
   crec\xEDa con su contenido por encima del canvas (100dvh + overflow:clip) y el frame scroll="true" del slot nunca
   desbordaba \u2192 lo que quedaba por debajo de la pantalla (paginador de una lista, final de una ficha larga) era
   inalcanzable. Mismo criterio que .xone-coll > .xone-group y .xone-coll-center > .xone-group (WEAL-418). */
.xone-canvas > .xone-group.xone-layout-fill { flex: 1 1 auto; min-height: 0; }
/* OPT-IN por coll de layout: <coll web-layout-name="\u2026" web-layout-fill="true"> \u2192 htmlLowering a\xF1ade xone-layout-fill
   al section.xone-group del layout. Sin el atributo, el group sigue como siempre (crece con su contenido): as\xED los
   proyectos existentes con web-layout (Alivia) no cambian de geometr\xEDa al actualizar la plataforma. */
/* [WLDA-F7] Un frame scroll="true" VERTICAL y SIN altura (height="" | "-2") recibe flex:1 1 auto; min-height:0 del
   lowering MPA (visualStyle "Scroll") y, si el proyecto opta (app.xml web-scroll-fill="true"), el marcador
   data-xone-scroll-fill="1" (frameRenderer). El runtime SPA (xml-renderer) no lo emite. Su fila envolvente .xone-row segu\xEDa en
   flex:0 0 auto cuando tiene hermanos (cabecera + men\xFA + contenido en el mismo frame columna): la fila crec\xEDa con el
   contenido, el frame nunca desbordaba y no hab\xEDa scroll. La fila cuyo \xFAnico hijo es ese frame absorbe el resto de la
   columna, que es lo que ese frame declara querer.
   \xB7 Se engancha al MARCADOR y no a [style*="overflow-y: auto"]: un frame scroll="true" CON altura literal
     (height="60%") tambi\xE9n lleva overflow-y:auto pero NO debe robar espacio a sus hermanos.
   \xB7 :where() deja la especificidad en (0,1,0) = la de .xone-row base (gana por orden), y por debajo de la gravity
     .xone-frame[data-xone-align*=\u2026] > .xone-row { flex:0 0 auto } (0,3,0), que sigue mandando.
   \xB7 El marcador solo lo emite el lowering MPA si el proyecto opta (app.xml web-scroll-fill="true"): sin \xE9l, ning\xFAn
     frame lo lleva y esta regla no casa nada \u2192 los proyectos existentes no cambian. */
.xone-row:where(:has(> .xone-frame[data-xone-scroll-fill]:only-child)) { flex: 1 1 auto; min-height: 0; }
.xone-fixed { display: flex; flex-direction: column; flex: 0 0 auto; }
.xone-fixed-left, .xone-fixed-right { min-width: 0; }
.xone-fixed[hidden] { display: none; }
/* Banda con un grupo fixed de altura de relleno (height=100% / -1). El emisor
   (fixedGroupsRenderer) le pone esta clase para que la banda CREZCA en vez de
   medir contenido: as\xED el height:100% del grupo resuelve contra una caja con
   alto real y no colapsa a 0. Paridad Android "fixed group = alto disponible"
   (EditViewHyper.createFixedGroup mide contra getMaxScreenHeight()). Caso raro:
   comparte el espacio del coll con el centro en vez de taparlo por completo. */
.xone-fixed-fill { flex: 1 1 auto; min-height: 0; }

/* \u2500\u2500 Grupos drawer (F4): panel off-canvas + scrim \u2500\u2500
   El contenido principal NO se desplaza (el drawer va position:fixed por encima).
   El JS solo togglea .is-open; el slide es por transform. width por grupo via
   --xone-drawer-width (default 100% = pantalla completa, paridad doc 01 \xA72.4.2). */
.xone-drawer {
    position: fixed; top: 0; bottom: 0; z-index: 1000;
    width: var(--xone-drawer-width, 100%); max-width: 100%;
    display: flex; flex-direction: column;
    background: #fff; will-change: transform;
    transition: transform 0.25s ease; overscroll-behavior: contain;
}
.xone-drawer-left  { left: 0;  transform: translateX(-100%); }
.xone-drawer-right { right: 0; transform: translateX(100%); }
.xone-drawer.is-open { transform: translateX(0); }
.xone-drawer[hidden] { display: none; }
.xone-drawer > .xone-group { flex: 1 1 auto; min-height: 0; }
.xone-drawer-scrim {
    position: fixed; inset: 0; z-index: 999;
    background: rgb(0 0 0 / 0.32);
    opacity: 0; transition: opacity 0.25s ease;
}
.xone-drawer-scrim.is-open { opacity: 1; }
.xone-drawer-scrim[hidden] { display: none; }
@media (prefers-reduced-motion: reduce) {
    .xone-drawer, .xone-drawer-scrim { transition: none; }
}
`;

// src/canvasSizer.ts
function installCanvasSizer(canvas) {
  const noop = () => {
  };
  if (typeof window === "undefined" || typeof ResizeObserver === "undefined" || !canvas) {
    return noop;
  }
  const mode = canvas.getAttribute("data-responsive");
  const isAuto = mode === "auto";
  const styles = window.getComputedStyle(canvas);
  const factorRaw = Number.parseFloat(styles.getPropertyValue("--xone-font-factor"));
  const fontFactor = Number.isFinite(factorRaw) && factorRaw > 0 ? factorRaw : isAuto ? 1 : 7;
  if (!isAuto) {
    const base = Number.parseFloat(styles.getPropertyValue("--xone-base-width"));
    if (!Number.isFinite(base) || base <= 0) return noop;
    const apply2 = () => {
      const w = canvas.clientWidth;
      if (w <= 0) return;
      const unit = w / base;
      canvas.style.setProperty("--xone-p-unit", `${unit}px`);
      canvas.style.setProperty("--xone-font-unit", `${fontFactor * unit}px`);
    };
    apply2();
    const ro2 = new ResizeObserver(apply2);
    ro2.observe(canvas);
    return () => ro2.disconnect();
  }
  let baseW = 0;
  const ensureBase = () => {
    if (baseW > 0) return true;
    const m = measureNaturalBase(canvas, fontFactor);
    if (m.width <= 0) return false;
    baseW = m.width;
    canvas.style.setProperty("--xone-base-width", String(baseW));
    if (m.height > 0) canvas.style.setProperty("--xone-base-height", String(m.height));
    return true;
  };
  const apply = () => {
    if (!ensureBase()) return;
    const w = canvas.clientWidth;
    if (w <= 0) return;
    const unit = w / baseW;
    canvas.style.setProperty("--xone-p-unit", `${unit}px`);
    canvas.style.setProperty("--xone-font-unit", `${fontFactor * unit}px`);
  };
  apply();
  const ro = new ResizeObserver(apply);
  ro.observe(canvas);
  return () => ro.disconnect();
}
function measureNaturalBase(canvas, fontFactor) {
  const s = canvas.style;
  const saved = {
    containerType: s.getPropertyValue("container-type"),
    width: s.width,
    maxWidth: s.maxWidth,
    height: s.height,
    minHeight: s.minHeight,
    overflow: s.overflow,
    flex: s.flex,
    alignSelf: s.alignSelf,
    pUnit: s.getPropertyValue("--xone-p-unit"),
    fontUnit: s.getPropertyValue("--xone-font-unit")
  };
  s.setProperty("--xone-p-unit", "1px");
  s.setProperty("--xone-font-unit", `${fontFactor}px`);
  s.setProperty("container-type", "normal");
  s.width = "max-content";
  s.maxWidth = "none";
  s.height = "auto";
  s.minHeight = "0";
  s.overflow = "visible";
  s.flex = "0 0 auto";
  s.alignSelf = "flex-start";
  const rect = canvas.getBoundingClientRect();
  const width = Math.round(rect.width);
  const height = Math.round(rect.height);
  restoreProp(s, "container-type", saved.containerType);
  s.width = saved.width;
  s.maxWidth = saved.maxWidth;
  s.height = saved.height;
  s.minHeight = saved.minHeight;
  s.overflow = saved.overflow;
  s.flex = saved.flex;
  s.alignSelf = saved.alignSelf;
  restoreProp(s, "--xone-p-unit", saved.pUnit);
  restoreProp(s, "--xone-font-unit", saved.fontUnit);
  return { width, height };
}
function restoreProp(s, name, prev) {
  if (prev) s.setProperty(name, prev);
  else s.removeProperty(name);
}
function installCanvasSizerFromDocument(root = typeof document !== "undefined" ? document : void 0) {
  if (!root || typeof root.querySelector !== "function") {
    return () => {
    };
  }
  const canvas = root.querySelector(".xone-canvas");
  return canvas ? installCanvasSizer(canvas) : () => {
  };
}
export {
  FORBIDDEN_UNITS,
  LAYOUT_PRIMITIVES_CSS,
  VALID_PLATFORMS,
  XONE_CSS_ERROR_CODES,
  XONE_FONTSIZE_OFFSET,
  XoneCssError,
  assertValidPlatformTag,
  installCanvasSizer,
  installCanvasSizerFromDocument,
  matchesPlatform,
  parseSelector,
  parseXoneCss,
  platformTagToMediaQuery,
  resolveExtends,
  selectorKey,
  selectorToCss,
  transformColors,
  transformToStandardCss,
  transformUnits
};
//# sourceMappingURL=index.js.map